import {
  Box,
  Typography,
  TextField,
  CircularProgress,
  IconButton,
  InputAdornment,
  FormHelperText,
} from "@mui/material";
import React from "react";
import TreffasStyle from "src/utils/TreffasStyle";
import { StaticDatePicker } from "@mui/x-date-pickers";
import { useTranslation } from "react-i18next";
import Timezone from "src/utils/Timezone";
import StepperGuide from "src/components/StepperGuide";
import TimezonePicker from "../../timezone-picker";
import { ArrowDropDown } from "@mui/icons-material";
import TreffasTextField from "src/components/TreffasTextField";
import RenderTimeSlots from "src/views/general/booking-components/RenderTimeSlots";
import NoAvailableTime from "src/views/general/booking-components/NoAvailableTime";
import { useDateAndTimePicker } from "src/views/hooks/useDateAndTimePicker";

const DateAndTime = ({
  errors,
  setSelectedTimezone,
  selectedTimezone,
  sessionList,
  selectedSessionTime,
  setSessionSelectedTime,
  selectedOffer,
  setSessionList,
  selectedDate,
  setSelectedDate,
  setError,
  clearErrors,
  nextButtonRef,
  calendarMaxDate,
  userDateFormat,
  userTimeFormat,
  userMinimumBookingNotice,
  stepperGuideLength,
  bookingInfo,
  coachDetails,
  setOverFlowTimeToRender,
  setUnderFlowTimeToRender,
  isUseTimeRecord,
  setIsUseTimeRecord,
  timeRecords,
  setTimeRecords,
  setAllUpcommingAppt,
}) => {
  const { t } = useTranslation();
  const {
    filteredList,
    preparingSessionList,
    timezoneDialog,
    isTimezoneChange,
    timeHover,
    setTimeHover,
    shouldDisabledDate,
    handleSelectedDate,
    handleTimezoneChange,
    handleSelectedTime,
    handleMonthChange,
    openTimezoneDialog,
    closeTimezoneDialog,
    datePickerMinDate,
    datePickerMaxDate,
    pickerValue,
  } = useDateAndTimePicker({
    selectedOffer,
    coachDetails,
    selectedTimezone,
    selectedDate,
    setSelectedDate,
    setSelectedTimezone,
    sessionList,
    setSessionList,
    setSessionSelectedTime,
    setOverFlowTimeToRender,
    setUnderFlowTimeToRender,
    setAllUpcommingAppt,
    isUseTimeRecord,
    setIsUseTimeRecord,
    timeRecords,
    setTimeRecords,
    clearErrors,
    calendarMaxDate,
    excludeAppointmentId: bookingInfo?.id,
    userDateFormat,
    userTimeFormat,
    userMinimumBookingNotice,
    nextButtonRef,
    setError,
  });

  return (
    <>
      <Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "10px",
            pt: "58px",
          }}
        >
          <Box>
            <Typography
              sx={{
                fontFamily: "var(--font-grenettepromedium)",
                fontSize: "18px",
                lineHeight: "31px",
                alignItems: "center",
                color: "#060A2D",
              }}
            >
              {t("individual.booking.schedule.choose_schedule")}
            </Typography>

            <Typography
              sx={{
                fontWeight: 400,
                fontSize: "14px",
                lineHeight: "18px",
                color: "#878787",
                mt: "4px",
              }}
            >
              {t("individual.booking.schedule.choose_schedule.description", {
                package: selectedOffer?.name,
              })}
            </Typography>
          </Box>

          <Box>
            <StepperGuide length={stepperGuideLength} activeLength={[1]} />
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          mt: "48px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: { xs: "10px", md: "32px" },
            flexDirection: { xs: "column", md: "row" },
          }}
        >
          <Box className="new-static-calendar">
            <StaticDatePicker
              // disablePast
              minDate={datePickerMinDate}
              displayStaticWrapperAs="desktop"
              value={pickerValue}
              shouldDisableDate={shouldDisabledDate}
              onChange={handleSelectedDate}
              onMonthChange={handleMonthChange}
              renderInput={(params) => <TextField {...params} />}
              views={["day"]}
              inputFormat={userDateFormat}
              maxDate={datePickerMaxDate}
              showDaysOutsideCurrentMonth
            />
          </Box>

          {/* availabitlity */}
          <Box
            style={{
              width: "100%",
            }}
          >
            <Box mb={"20px"}>
              <Box mb={"10px"}>
                <Typography
                  sx={{
                    fontFamily: "var(--font-grenettepromedium)",
                    fontSize: "10px",
                    lineHeight: "11px",
                    letterSpacing: "0.07em",
                    color: "#A1A1A1",
                    textTransform: "uppercase",
                  }}
                >
                  {t("booking.label.select_timezone")}
                </Typography>
              </Box>

              <Box sx={{ px: "5px" }}>
                <Box>
                  <TreffasTextField
                    value={Timezone.getLabel(selectedTimezone) ?? ""}
                    sx={TreffasStyle.formInputLarge("autocomplete")}
                    placeholder={t("booking.placeholder.select_timezone")}
                    fullWidth
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <InputAdornment position="start">
                          <IconButton
                            size={"small"}
                            onClick={() => openTimezoneDialog()}
                          >
                            <ArrowDropDown />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Box>
                    <TimezonePicker
                      dialog={timezoneDialog}
                      close={closeTimezoneDialog}
                      selectedTimezone={selectedTimezone}
                      onChange={handleTimezoneChange}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>

            <Box>
              <Box mb={"10px"}>
                <Typography
                  sx={{
                    fontFamily: "var(--font-grenettepromedium)",
                    fontSize: "10px",
                    lineHeight: "11px",
                    letterSpacing: "0.07em",
                    color: "#A1A1A1",
                    textTransform: "uppercase",
                  }}
                >
                  {t("booking.placeholder.time_slots")}
                </Typography>

                {Boolean(errors?.time) && (
                  <FormHelperText error>
                    {" "}
                    {errors?.time?.message}{" "}
                  </FormHelperText>
                )}
              </Box>

              {selectedDate === null || selectedDate === "" ? (
                <NoAvailableTime
                  message={t("booking.error.required.select_date")}
                  fontSize="12px"
                />
              ) : (
                <Box
                  className="session-time-scroll"
                  sx={{
                    height: "338px",
                    overflowY: "auto",
                    width: "392px",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "10px",
                    }}
                  >
                    {preparingSessionList ? (
                      <Box
                        sx={{
                          width: "100%",
                          textAlign: "center",
                          mt: "10px",
                        }}
                      >
                        <CircularProgress size={20} />
                      </Box>
                    ) : filteredList?.length > 0 ? (
                      filteredList?.map((time, key) => {
                        return (
                          <Box
                            sx={{
                              width: "120px",
                            }}
                            key={key}
                          >
                            <RenderTimeSlots
                              selectedSessionTime={selectedSessionTime}
                              time={time}
                              handleSelectedTime={handleSelectedTime}
                              timeHover={timeHover}
                              setTimeHover={setTimeHover}
                              userTimeFormat={userTimeFormat}
                              _key={key}
                            />
                          </Box>
                        );
                      })
                    ) : (
                      <NoAvailableTime
                        fontSize="12px"
                        message={
                          isTimezoneChange
                            ? t(
                                "individual.booking.schedule.choose_schedule.select_date_again"
                              )
                            : t(
                                "booking.error.required.no_time_on_selected_date"
                              )
                        }
                      />
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default DateAndTime;
